import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Paper, FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { addDays, format, differenceInBusinessDays, getDay, isSameDay } from 'date-fns';

// Helper function to check if a date is a weekend
const isWeekend = (date: Date): boolean => {
  const day = getDay(date);
  return day === 0 || day === 6; // Sunday or Saturday
};

const WorkDaysCalc: React.FC = () => {
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [includeSaturday, setIncludeSaturday] = useState<boolean>(false);
  const [includeSunday, setIncludeSunday] = useState<boolean>(false);
  const [result, setResult] = useState<string>('');

  const calculateWorkDays = () => {
    if (!startDate || !endDate) {
      setResult('Please select both a start and an end date.');
      return;
    }

    if (endDate < startDate) {
      setResult('End date cannot be earlier than start date.');
      return;
    }
    if (isSameDay(startDate, endDate)){
        setResult('0 work days (start and end dates are the same).');
        return;
    }

    let workDays = 0;
    let currentDate = new Date(startDate);

    // Iterate day by day from startDate to endDate (exclusive of endDate for counting full days between)
    while (currentDate < endDate) {
        const dayOfWeek = getDay(currentDate);
        let isWorkDay = true;

        if (dayOfWeek === 6 && !includeSaturday) { // Saturday
            isWorkDay = false;
        } else if (dayOfWeek === 0 && !includeSunday) { // Sunday
            isWorkDay = false;
        }
        
        if(isWorkDay) {
            workDays++;
        }
        currentDate = addDays(currentDate, 1);
    }

    setResult(`Total work days: ${workDays}`);
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Calculate Work Days
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
        <DatePicker
          label="Start Date"
          value={startDate}
          onChange={setStartDate}
          slots={{ textField: (params) => <TextField {...params} fullWidth /> }}
        />
        <DatePicker
          label="End Date"
          value={endDate}
          onChange={setEndDate}
          slots={{ textField: (params) => <TextField {...params} fullWidth /> }}
          minDate={startDate || undefined} // Prevent end date from being before start date
        />
        <FormGroup row sx={{mt: 1}}>
          <FormControlLabel
            control={<Checkbox checked={includeSaturday} onChange={(e) => setIncludeSaturday(e.target.checked)} />}
            label="Include Saturdays"
          />
          <FormControlLabel
            control={<Checkbox checked={includeSunday} onChange={(e) => setIncludeSunday(e.target.checked)} />}
            label="Include Sundays"
          />
        </FormGroup>
        <Button variant="contained" onClick={calculateWorkDays} disabled={!startDate || !endDate}>
          Calculate Work Days
        </Button>
        {result && (
          <Typography variant="subtitle1" sx={{ mt: 2 }}>
            Result: <strong>{result}</strong>
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default WorkDaysCalc;
